import {
  InformationOutline18Icon,
  LinkOutOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { ISirkModuleForShifts } from "features/CenterContent/RoleContent/ISirk/ISirkModuleForShifts";
import {
  BusModule,
  PanelHeaderDivider,
  PanelHorizontalScroll,
  PanelModuleGrid,
  PanelScrollArea,
  PanelScrollContent,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/altTransportPanelLayout";
import { ShiftSchedule } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ShiftSchedule";
import {
  VehicleSummary,
  VehicleTitle,
} from "features/CenterContent/VehicleDetails/shared/VehicleSummary";
import { FC, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Module } from "shared/components/Module";
import {
  Button,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  tokens,
} from "shared/components/Spor";
import { AlternativeTransportVehicle } from "shared/types/alternativeTransport";
import { LANDING } from "shared/utils/routes";
import { useDropsRole } from "stores/useDropsRole";
import { useTheme } from "styled-components";

interface AlternativeTransportLeftPanelProps {
  vehicle: AlternativeTransportVehicle;
}

export const ShiftLogInfoPopover: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();

  return (
    <Popover
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
      autoFocus={false}
      positioning={{ placement: "top" }}
    >
      <PopoverTrigger>
        <IconButton
          variant="ghost"
          size="xs"
          icon={<InformationOutline18Icon />}
          aria-label="Informasjon"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
        />
      </PopoverTrigger>
      <PopoverContent
        borderRadius={tokens.default.size["border-radius"].sm}
        padding={tokens.default.size.spacing[1]}
        color={theme.colorBackgroundMain}
      >
        Skift-loggen viser kun I-sirk-meldinger som er relevante for dette
        skiftet. Se fullstendig oversikt i I-sirk-loggen.
      </PopoverContent>
    </Popover>
  );
};

export const AlternativeTransportLeftPanel = ({
  vehicle,
}: AlternativeTransportLeftPanelProps) => {
  const navigate = useNavigate();
  const { role } = useDropsRole();

  const { workShiftPlan, operatorName } = vehicle;
  const { workShiftTrips, shiftId, shiftDate, isirkId } = workShiftPlan ?? {};

  const firstDeparture = workShiftTrips?.at(0)?.scheduledDepartureTimeOrigin;
  const lastArrival = workShiftTrips?.at(-1)?.scheduledArrivalTimeDestination;

  return (
    <BusModule
      modulePosition="left"
      title={
        <VehicleTitle
          title={`Skift ${shiftId ?? ""}`}
          externalSystem={vehicle.externalSystem}
        />
      }
      sectionState={{
        isEmpty: !vehicle.workShiftPlan,
        emptyReason: "Adgang er ikke tilknyttet skift",
      }}
    >
      <PanelHeaderDivider>
        {operatorName && workShiftTrips ? (
          <VehicleSummary
            variant="shift"
            type={vehicle.type}
            operatorName={operatorName}
            driverPhoneNumber={vehicle.driverPhoneNumber}
            startTime={firstDeparture}
            endTime={lastArrival}
          />
        ) : (
          <NoDataMessage reason="Det finnes ikke skiftinfo denne bussen" />
        )}
      </PanelHeaderDivider>
      <PanelHorizontalScroll>
        <PanelScrollContent>
          <PanelScrollArea>
            <PanelModuleGrid>
              <Module title="Skiftplan">
                <ShiftSchedule vehicle={vehicle} />
              </Module>

              <Module
                title="Skift-logg"
                size="md"
                specification={isirkId || "ukjent"}
                action1={
                  <Button
                    size="xs"
                    fontSize="xs"
                    variant="ghost"
                    onClick={() =>
                      navigate(
                        generatePath(LANDING, {
                          roleIdParam: role,
                          tabIdParam: "start",
                        }),
                      )
                    }
                  >
                    I-sirk-logg <LinkOutOutline18Icon />
                  </Button>
                }
                action2={<ShiftLogInfoPopover />}
              >
                <ISirkModuleForShifts
                  selectedDate={shiftDate?.toString() ?? ""}
                  shiftId={shiftId ?? ""}
                  operatorName={operatorName || ""}
                />
              </Module>
            </PanelModuleGrid>
          </PanelScrollArea>
        </PanelScrollContent>
      </PanelHorizontalScroll>
    </BusModule>
  );
};
